import { addWeeks, endOfMonth, endOfWeek, endOfYear, format, getDaysInMonth, isWithinInterval, set, startOfMonth, startOfWeek, subWeeks, toDate } from "date-fns";

import { DaysRange, HoursRange, IWeek } from "../types/date.types";

export class DateUtil {
    public static readonly WEEKDAY_START = 1;
    public static readonly TIME_SEPARATOR = ":";
    public static readonly ALL_DAY_START_TIME = "00:00";
    public static readonly ALL_DAY_END_TIME = "24:00";

    public static toIsoDate(givenDate: Date | string | number): string {
        return format(givenDate, "yyyy-MM-dd");
    }

    public static isDateInRange(givenDate: Date | string | number, range: IWeek): boolean {
        return isWithinInterval(givenDate, { start: toDate(range.start), end: toDate(range.end) });
    }

    public static calculateWeekStartAndEndDates(anyDayOfWeek: Date | string | number = new Date()): IWeek {
        const start = startOfWeek(anyDayOfWeek, { weekStartsOn: DateUtil.WEEKDAY_START });
        const end = endOfWeek(anyDayOfWeek, { weekStartsOn: DateUtil.WEEKDAY_START });
        return { start, end };
    }

    public static calculateMonthStartAndEndDates(anyDayOfMonth: Date | string | number = new Date()): DaysRange {
        const start = startOfMonth(anyDayOfMonth);
        const end = endOfMonth(anyDayOfMonth);
        return { start, end };
    }

    public static getWeekOfToday(): IWeek {
        return DateUtil.calculateWeekStartAndEndDates();
    }

    public static getWeekPerIndex(index: number): IWeek {
        const refDate = addWeeks(new Date(), index);
        return DateUtil.calculateWeekStartAndEndDates(refDate);
    }

    public static getPreviousWeek(date?: Date | string | number): IWeek {
        const d = date ? toDate(date) : new Date();
        const noon = set(d, { hours: 12, minutes: 0, seconds: 0, milliseconds: 0 });
        return DateUtil.calculateWeekStartAndEndDates(subWeeks(noon, 1));
    }

    public static getNextWeek(date?: Date | string | number): IWeek {
        const d = date ? toDate(date) : new Date();
        const noon = set(d, { hours: 12, minutes: 0, seconds: 0, milliseconds: 0 });
        return DateUtil.calculateWeekStartAndEndDates(addWeeks(noon, 1));
    }

    /**
     * @param today The current date
     * @returns A timestamp of the last day of the week.
     */
    public static getLastDayOfWeek(today: Date | string | number): number {
        const date = toDate(today);
        return endOfWeek(date, { weekStartsOn: DateUtil.WEEKDAY_START }).getTime();
    }

    /**
     * @param today The current date
     * @returns A timestamp of the last day of the month.
     */
    public static getLastDayOfMonth(today: Date | string | number): number {
        const date = toDate(today);
        return endOfMonth(date).getTime();
    }

    /**
     * @param today The current date
     * @returns A timestamp of the last day of the year.
     */
    public static getLastDayOfYear(today: Date | string | number): number {
        const date = toDate(today);
        return endOfYear(date).getTime();
    }

    public static daysInMonth(year: number, month1to12: number): number {
        return getDaysInMonth(new Date(year, month1to12 - 1));
    }

    public static withTime(timeHHmm: string, baseDate?: Date | string | number): Date {
        const timeHHmmParts = timeHHmm.split(DateUtil.TIME_SEPARATOR);
        if (timeHHmmParts.length !== 2) {
            throw new Error("timeHHmm must be in the format HH:mm");
        }
        const hour = parseInt(timeHHmmParts[0], 10);
        const minute = parseInt(timeHHmmParts[1], 10);
        const second = 0;
        return set(baseDate, { hours: hour, minutes: minute, seconds: second });
    }

    public static getHoursRange(starts: Date, ends: Date): HoursRange {
        const localeStartTimeParts = starts.toLocaleTimeString().split(DateUtil.TIME_SEPARATOR);
        const localeEndTimeParts = ends.toLocaleTimeString().split(DateUtil.TIME_SEPARATOR);
        if (localeStartTimeParts.length < 2 || localeEndTimeParts.length < 2) {
            throw new Error("timeHHmm must be in the format HH:mm");
        }
        const startTime = `${localeStartTimeParts[0]}${DateUtil.TIME_SEPARATOR}${localeStartTimeParts[1]}`;
        const endTime = `${localeEndTimeParts[0]}${DateUtil.TIME_SEPARATOR}${localeEndTimeParts[1]}`;
        return {
            startTime,
            endTime
        };
    }
}
